import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { Backdrop } from '@mui/material';

interface DrawerState {
  id: string;
  open: boolean;
  anchor: 'primary' | 'secondary';
  onClose?: () => void;
}

interface DrawerProviderContextType {
  drawers: DrawerState[];
  registerDrawer: (
    id: string,
    anchor: 'primary' | 'secondary',
    onClose?: () => void
  ) => void;
  unregisterDrawer: (id: string) => void;
  openDrawer: (id: string) => void;
  closeDrawer: (id: string) => void;
  closeAllDrawers: () => void;
  isAnyDrawerOpen: boolean;
  getPrimaryWidth: () => string;
  getSecondaryWidth: () => string;
}

const DrawerProviderContext = createContext<DrawerProviderContextType | null>(
  null
);

export const useDrawerProvider = () => {
  const context = useContext(DrawerProviderContext);
  if (!context) {
    throw new Error('useDrawerProvider must be used within a DrawerProvider');
  }
  return context;
};
interface DrawerProviderProps {
  children: ReactNode;
  onBackdropClick?: () => void;
}

/**
 * DrawerProvider manages multiple drawers and their shared backdrop.
 * Wrap your application or component tree to enable drawer functionality.
 *
 * Features:
 * - Automatic backdrop management for multiple drawers
 * - Smart backdrop click behavior
 * - Z-index coordination between drawers
 * - Support for drawers rendered anywhere in the component tree
 *
 * Usage:
 * ```tsx
 * <DrawerProvider>
 *   <YourApp />
 *   <Drawer open={open} onClose={handleClose} title="Settings">
 *     <SettingsContent />
 *   </Drawer>
 * </DrawerProvider>
 * ```
 *
 * Backdrop behavior:
 * - Single drawer open: backdrop click closes the drawer
 * - Multiple drawers open: backdrop click does nothing (prevents accidental closure)
 */
export const DrawerProvider = ({
  children,
  onBackdropClick,
}: DrawerProviderProps) => {
  const [drawers, setDrawers] = useState<DrawerState[]>([]);

  const registerDrawer = useCallback(
    (id: string, anchor: 'primary' | 'secondary', onClose?: () => void) => {
      setDrawers(prev => {
        if (prev.find(d => d.id === id)) return prev;
        return [...prev, { id, open: false, anchor, onClose }];
      });
    },
    []
  );

  const unregisterDrawer = useCallback((id: string) => {
    setDrawers(prev => prev.filter(d => d.id !== id));
  }, []);

  const openDrawer = useCallback((id: string) => {
    setDrawers(prev => {
      const drawer = prev.find(d => d.id === id);
      if (!drawer || drawer.open) return prev; // Already open or doesn't exist
      return prev.map(d => (d.id === id ? { ...d, open: true } : d));
    });
  }, []);

  const closeDrawer = useCallback((id: string) => {
    setDrawers(prev => {
      const drawer = prev.find(d => d.id === id);
      if (!drawer || !drawer.open) return prev; // Already closed or doesn't exist
      return prev.map(d => (d.id === id ? { ...d, open: false } : d));
    });
  }, []);

  const closeAllDrawers = useCallback(() => {
    setDrawers(prev => prev.map(d => ({ ...d, open: false })));
    onBackdropClick?.();
  }, [onBackdropClick]);

  const handleBackdropClick = useCallback(() => {
    const openDrawers = drawers.filter(d => d.open);

    if (openDrawers.length === 1) {
      // Close the single open drawer by calling its onClose callback
      const drawer = openDrawers[0];
      if (drawer.onClose) {
        drawer.onClose();
      } else {
        // Fallback to internal state update
        closeDrawer(drawer.id);
      }
    }
    // If multiple drawers are open, do nothing

    onBackdropClick?.();
  }, [drawers, closeDrawer, onBackdropClick]);

  const isAnyDrawerOpen = drawers.some(d => d.open);

  // Width calculation functions
  const getPrimaryWidth = useCallback(() => {
    const openDrawers = drawers.filter(d => d.open);
    const hasSecondaryOpen = openDrawers.some(d => d.anchor === 'secondary');
    return hasSecondaryOpen ? '55%' : '95%';
  }, [drawers]);

  const getSecondaryWidth = useCallback(() => {
    const openDrawers = drawers.filter(d => d.open);
    const hasSecondaryOpen = openDrawers.some(d => d.anchor === 'primary');
    return hasSecondaryOpen ? '40%' : '65%';
  }, [drawers]);

  const contextValue: DrawerProviderContextType = useMemo(
    () => ({
      drawers,
      registerDrawer,
      unregisterDrawer,
      openDrawer,
      closeDrawer,
      closeAllDrawers,
      isAnyDrawerOpen,
      getPrimaryWidth,
      getSecondaryWidth,
    }),
    [
      drawers,
      registerDrawer,
      unregisterDrawer,
      openDrawer,
      closeDrawer,
      closeAllDrawers,
      isAnyDrawerOpen,
      getPrimaryWidth,
      getSecondaryWidth,
    ]
  );

  return (
    <DrawerProviderContext.Provider value={contextValue}>
      {children}

      {/* Single managed backdrop */}
      <Backdrop
        open={isAnyDrawerOpen}
        onClick={handleBackdropClick}
        sx={{
          zIndex: theme => theme.zIndex.drawer - 1,
        }}
      />
    </DrawerProviderContext.Provider>
  );
};

// Export DrawerManager as an alias for backward compatibility
export const DrawerManager = DrawerProvider;
